<script setup lang="ts">
/**
 * Left annotation pane — mirrors GameAreaSettingsPane (right pane) almost
 * exactly: floats over the score, only its width animates, dismisses on
 * outside pointerdown or a left-ward drag past a threshold. Mirrored in
 * anchor edge and drag direction since this pane opens from the left. See
 * useEdgeDragDismiss / useOutsidePointerDismiss for the shared mechanics.
 *
 * Holds the fixed palette of annotation characters as draggable cells —
 * dragging one onto the sheet places (or replaces) an annotation on the
 * nearest note; see useNoteAnnotations for the actual drag/drop mechanics.
 */
import { useEdgeDragDismiss } from '@/composables/useEdgeDragDismiss';
import { useOutsidePointerDismiss } from '@/composables/useOutsidePointerDismiss';
import { ANNOTATION_CHARS, useInjectNoteAnnotations } from '@/composables/useNoteAnnotations';

const open = defineModel<boolean>('open', { required: true });

const noteAnnotations = useInjectNoteAnnotations();

const { dragTranslateX, isDragging, handlers: dragHandlers } = useEdgeDragDismiss(open, {
  anchor: 'left',
  isInteractiveTarget: (target) =>
    target instanceof HTMLElement && !!target.closest('.annotation-cell'),
});

useOutsidePointerDismiss(open, ['.annotation-pane', '.annotation-toggle-button']);

function handleCellPointerDown(char: typeof ANNOTATION_CHARS[number], event: PointerEvent) {
  event.preventDefault();
  noteAnnotations.startDragNewCell(char, event);
}
</script>

<template>
  <div
    class="annotation-pane"
    :class="{
      'annotation-pane--open': open,
      'annotation-pane--dragging': isDragging,
      // Collapsed instead of just hidden while a cell/annotation is being
      // dragged, so the score underneath it is fully visible/reachable for
      // placing the drop, not just dimmed behind the pane.
      'annotation-pane--hidden-for-drag': noteAnnotations.isDragging.value,
    }"
    :style="{ transform: dragTranslateX ? `translateX(${dragTranslateX}px)` : undefined }"
    :inert="!open || noteAnnotations.isDragging.value"
    role="group"
    aria-label="Note annotations"
    v-on="dragHandlers"
  >
    <div class="annotation-pane-content">
      <p class="annotation-pane-hint">
        Drag a cell onto a note. Drag it to the trash to remove.
      </p>
      <div class="annotation-cell-grid">
        <button
          v-for="char in ANNOTATION_CHARS"
          :key="char"
          type="button"
          class="annotation-cell"
          @pointerdown="handleCellPointerDown(char, $event)"
        >
          {{ char }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.annotation-pane {
  width: 0;
  height: 100%;
  overflow: hidden;
  background: var(--color-secondary);
  box-shadow: 8px 0 24px rgba(0, 0, 0, 0.35);
  transition: width 0.22s ease, transform 0.2s ease, opacity 0.15s ease;
}

.annotation-pane--open {
  width: 280px;
}

/* Stays at its open width during a drag (no layout shift) — invisible and
   non-interactive instead: opacity fades it out immediately, but z-index
   only drops behind the score once that fade finishes, so it never visibly
   pops out of the stacking order mid-fade. (`:inert` above is what actually
   blocks pointer/focus the whole time; the z-index drop is just visual.) */
.annotation-pane--hidden-for-drag {
  opacity: 0;
  z-index: -1 !important;
  transition: width 0.22s ease, transform 0.2s ease, opacity 0.15s ease, z-index 0s linear 0.15s;
}

/* No transition while actively dragging, so the pane tracks the finger with
   zero lag — mirrors MainNav's mobile-menu-container--dragging. Width never
   actually changes mid-drag (it only flips on open/close, which happens
   after isDragging is already back to false), so dropping both properties
   here is equivalent to only dropping transform, just simpler. */
.annotation-pane--dragging {
  transition: none;
}

.annotation-pane-content {
  width: 280px;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.annotation-pane-hint {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-text-secondary);
}

.annotation-cell-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.annotation-cell {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-surface-hover);
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.annotation-cell:active {
  cursor: grabbing;
  background: var(--color-surface-hover);
}
</style>
